import React, { useState, useEffect, useCallback } from "react";
import { extendAdminSession } from "../lib/adminAuth";

const AdminIdleTimeout = ({ onTimeout, children }) => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Get idle timeout from environment (default 45 minutes)
  const IDLE_TIMEOUT_MS =
    (parseInt(process.env.REACT_APP_ADMIN_IDLE_MINUTES) || 45) * 60 * 1000;
  const WARNING_TIME_MS = 2 * 60 * 1000; // 2 minutes warning

  // Track user activity
  const updateActivity = useCallback(() => {
    const now = Date.now();
    setLastActivity(now);

    // Store in non-httpOnly cookie for server to read
    document.cookie = `bb_admin_last=${new Date(now).toISOString()}; path=/; SameSite=Lax`;

    // Hide warning if activity detected
    if (showWarning) {
      setShowWarning(false);
    }
  }, [showWarning]);

  // Activity event handlers
  useEffect(() => {
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    const throttledUpdateActivity = throttle(updateActivity, 30000); // Update at most every 30 seconds

    events.forEach((event) => {
      document.addEventListener(event, throttledUpdateActivity, true);
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, throttledUpdateActivity, true);
      });
    };
  }, [updateActivity]);

  // Idle timeout timer
  useEffect(() => {
    const checkIdleTimeout = () => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivity;
      const timeUntilTimeout = IDLE_TIMEOUT_MS - timeSinceActivity;

      if (timeUntilTimeout <= 0) {
        // Session timed out
        setShowWarning(false);
        if (onTimeout) {
          onTimeout();
        }
        return;
      }

      if (timeUntilTimeout <= WARNING_TIME_MS && !showWarning) {
        // Show warning
        setShowWarning(true);
        setTimeLeft(Math.ceil(timeUntilTimeout / 1000));
      }

      if (showWarning) {
        setTimeLeft(Math.ceil(timeUntilTimeout / 1000));
      }
    };

    const interval = setInterval(checkIdleTimeout, 1000);
    return () => clearInterval(interval);
  }, [lastActivity, showWarning, IDLE_TIMEOUT_MS, WARNING_TIME_MS, onTimeout]);

  // Handle session extension
  const handleStaySignedIn = async () => {
    try {
      const result = await extendAdminSession();
      if (result.success) {
        setLastActivity(Date.now());
        setShowWarning(false);

        // Update cookie
        document.cookie = `bb_admin_last=${new Date().toISOString()}; path=/; SameSite=Lax`;
      } else {
        // Extension failed, probably need to re-authenticate
        if (onTimeout) {
          onTimeout();
        }
      }
    } catch (error) {
      console.error("Session extension error:", error);
      if (onTimeout) {
        onTimeout();
      }
    }
  };

  // Format time display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <>
      {children}

      {/* Idle Timeout Warning Modal */}
      {showWarning && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
                <svg
                  className="h-6 w-6 text-yellow-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.828 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>

              <h3 className="text-lg leading-6 font-medium text-gray-900 mt-4">
                Session Timeout Warning
              </h3>

              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  You'll be signed out in{" "}
                  <span className="font-mono font-bold text-red-600">
                    {formatTime(timeLeft)}
                  </span>{" "}
                  due to inactivity.
                </p>
              </div>

              <div className="flex justify-center space-x-4 px-4 py-3">
                <button
                  onClick={handleStaySignedIn}
                  className="px-4 py-2 bg-orange-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  Stay Signed In
                </button>
                <button
                  onClick={onTimeout}
                  className="px-4 py-2 bg-gray-300 text-gray-700 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Sign Out Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Throttle utility function
function throttle(func, limit) {
  let inThrottle;
  return function () {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

export default AdminIdleTimeout;
